import { useEffect, useMemo, useRef, useState } from 'react';
import { CartItemTypeEnum } from '../models/cart.model';
import { type CartItemInterface, type ConfigurableCartItem } from '../types/front.graphql';
import { useProductsSalableQuantityLazyQuery } from '@kidslabs/module-catalog';
import type { CartItemReferenceAction } from '../models';
import { useDebounce } from '@kidslabs/module-core';

/**
 * Stock availability information for a single SKU
 */
export interface StockAvailabilityInfo {
    /** Whether the product is salable with the requested quantity */
    isSalable: boolean;
    /** Available quantity in stock (null if not available) */
    availableQuantity: number | null;
    /** Loading state for this SKU */
    loading: boolean;
    /** Errors from the backend (if any) */
    errors: Array<{
        code: string;
        message: string;
    }> | null;
}

/**
 * Map of SKU to stock availability information
 */
export interface StockAvailabilityMap {
    [sku: string]: StockAvailabilityInfo;
}

interface SkuQuantityPair {
    sku: string;
    quantity: number;
}

/**
 * Input type for product salable quantity check
 */
interface ProductSalableQuantityInput {
    sku: string;
    request_qty: number;
}

// Placeholder for catalog module query - should be imported from module-catalog when available
// const useProductsSalableQuantityLazyQuery = () => {
//     const fetchStockBatch = async (_options: {variables: {items: ProductSalableQuantityInput[]}}) => {
//         // This is a placeholder. In actual implementation, this would call the GraphQL API
//         // Should be replaced with actual import from module-catalog
//         console.log('[useBatchProductStockAvailability] Placeholder query - needs actual implementation');
//         return {data: null, error: null};
//     };
//     return [fetchStockBatch] as const;
// };

/**
 * ✅ OPTIMIZED BATCH QUERY HOOK
 *
 * This hook uses the batch query endpoint to fetch stock availability for multiple SKUs
 * in a SINGLE request, solving the N+1 query problem.
 *
 * BACKEND SUPPORT:
 * - Uses `productsSalableQuantity(items: [ProductSalableQuantityInput!])`
 * - Returns detailed information: isSalable, availableQuantity, errors for each SKU
 *
 * FEATURES:
 * - ✅ Single batch query for all cart items (no N+1 problem)
 * - ✅ Returns detailed stock information:
 *   - isSalable: Whether product is available with requested quantity
 *   - availableQuantity: Exact quantity available in stock
 *   - errors: Backend errors with code and message
 * - ✅ Optimistic UI: Assumes available during loading
 * - ✅ Proper error handling with fallback
 *
 * ARCHITECTURE:
 * ```
 * CartItems (Parent)
 *   ├── useBatchProductStockAvailability() → Query ALL cart items once
 *   ├── StockAvailabilityProvider → Share data with children
 *   └── CartItemGroup (Children)
 *         └── useStockAvailabilityContext() → Consume shared data
 * ```
 *
 * USAGE:
 * ```typescript
 * // In CartItems component (Parent - queries all)
 * const allCartItems = [...]; // All items from all groups
 * const stockMap = useBatchProductStockAvailability(allCartItems);
 *
 * return (
 *   <StockAvailabilityProvider value={stockMap}>
 *     {cartItemGroups.map(group => (
 *       <CartItemGroup items={group} />
 *     ))}
 *   </StockAvailabilityProvider>
 * );
 *
 * // In CartItemGroup (Child - consumes from context)
 * const stockMap = useStockAvailabilityContext();
 * const stockInfo = stockMap[item.sku];
 *
 * // Access detailed stock info
 * if (!stockInfo.isSalable) {
 *   return (
 *     <OutOfStockMessage
 *       availableQuantity={stockInfo.availableQuantity}
 *       errors={stockInfo.errors}
 *     />
 *   );
 * }
 * ```
 *
 * RETURNED INFORMATION:
 * - `isSalable`: boolean - Can the requested quantity be fulfilled?
 * - `availableQuantity`: number | null - How many units are available?
 * - `loading`: boolean - Is stock data still loading?
 * - `errors`: Array<{code, message}> | null - Any backend errors
 *
 * @param cartItems - Array of cart items to check stock for
 * @returns Map of SKU to detailed stock availability information
 */
export const useBatchProductStockAvailability = (
    cartItems: CartItemInterface[],
    logicActions?: CartItemReferenceAction[]
): StockAvailabilityMap => {
    const [stockMap, setStockMap] = useState<StockAvailabilityMap>({});
    const [fetchStockBatch] = useProductsSalableQuantityLazyQuery();

    // Extract SKUs and quantities from logic actions
    const skuQuantityPairsFromLogicActions = useMemo<SkuQuantityPair[]>(() => {
        return (
            logicActions
                ?.map(action => {
                    return {
                        sku: action?.actionProduct?.sku ?? '',
                        quantity: action?.actionQty ?? 0,
                    };
                })
                .filter((pair): pair is SkuQuantityPair => pair.sku !== '') ?? []
        );
    }, [logicActions]);

    // Extract SKUs and quantities from cart items
    const skuQuantityPairsFromCartItems = useMemo<SkuQuantityPair[]>(() => {
        return cartItems
            .map(item => {
                const typename = (item as { __typename?: string })?.__typename;
                const isConfigurableItem =
                    typename === CartItemTypeEnum.CONFIGURABLE || typename === CartItemTypeEnum.CONFIGURABLE_LOCAL;

                const sku = isConfigurableItem
                    ? (item as ConfigurableCartItem)?.configured_variant?.sku
                    : item?.product?.sku;

                return {
                    sku: sku ?? '',
                    quantity: item?.quantity ?? 0,
                };
            })
            .filter((pair): pair is SkuQuantityPair => pair.sku !== '');
    }, [cartItems]);

    const skuQuantityPairs = useMemo<SkuQuantityPair[]>(
        () => [...skuQuantityPairsFromLogicActions, ...skuQuantityPairsFromCartItems],
        [skuQuantityPairsFromLogicActions, skuQuantityPairsFromCartItems]
    );

    // Convert to ProductSalableQuantityInput format
    const batchInput = useMemo<ProductSalableQuantityInput[]>(() => {
        return skuQuantityPairs.map(pair => ({
            sku: pair.sku,
            request_qty: pair.quantity,
        }));
    }, [skuQuantityPairs]);

    // Create stable key for dependency checking to prevent unnecessary re-fetches
    const batchInputKey = useMemo(() => {
        return batchInput.map(item => `${item.sku}:${item.request_qty}`).join('|');
    }, [batchInput]);

    const debouncedBatchInputKey = useDebounce(batchInputKey);

    const batchInputRef = useRef(batchInput);
    batchInputRef.current = batchInput;

    const buildInitialStockMap = (
        prev: StockAvailabilityMap,
        batchInput: ProductSalableQuantityInput[]
    ): StockAvailabilityMap => {
        return batchInput.reduce<StockAvailabilityMap>((map, input) => {
            const previous = prev[input.sku];

            map[input.sku] = {
                isSalable: previous?.isSalable ?? true,
                availableQuantity: previous?.availableQuantity ?? null,
                loading: true,
                errors: previous?.errors ?? null,
            };

            return map;
        }, {});
    };

    useEffect(() => {
        const input = batchInputRef.current;

        // Early return if no items to query
        if (input.length === 0) {
            setStockMap({});
            return;
        }

        let cancelled = false;

        // ✅ OPTIMIZED: Single batch query for all SKUs
        const fetchAllStockData = async () => {
            // Initialize optimistic loading state for all SKUs
            setStockMap(prev => buildInitialStockMap(prev, input));

            try {
                const { data, error: queryError } = await fetchStockBatch({
                    variables: {
                        items: input,
                    },
                });

                if (cancelled) {
                    return;
                }

                if (queryError) {
                    console.error('[useBatchProductStockAvailability] Query error:', queryError);
                    // Keep optimistic state on query error
                    setStockMap(prev => {
                        const updatedMap = { ...prev };
                        Object.keys(updatedMap).forEach(sku => {
                            updatedMap[sku] = {
                                ...updatedMap[sku],
                                loading: false,
                            };
                        });
                        return updatedMap;
                    });
                    return;
                }

                // Process batch response
                const stockResults =
                    (
                        data as {
                            productsSalableQuantity?: Array<{
                                sku?: string;
                                is_salable?: boolean;
                                available_quantity?: number;
                                errors?: Array<{ code?: string; message?: string } | null> | null;
                            } | null>;
                        } | null
                    )?.productsSalableQuantity ?? [];

                const updatedMap: StockAvailabilityMap = {};
                stockResults.forEach(result => {
                    if (!result?.sku) {
                        return;
                    }

                    updatedMap[result.sku] = {
                        isSalable: result.is_salable ?? false,
                        availableQuantity: result.available_quantity ?? null,
                        loading: false,
                        errors: result.errors
                            ? result.errors
                                  .filter((err): err is NonNullable<typeof err> => err !== null)
                                  .map(err => ({
                                      code: err.code ?? 'UNKNOWN_ERROR',
                                      message: err.message ?? 'An unknown error occurred',
                                  }))
                            : null,
                    };
                });

                setStockMap(updatedMap);
            } catch (error) {
                if (cancelled) {
                    return;
                }
                console.error('[useBatchProductStockAvailability] Unexpected error:', error);
                // Keep optimistic state on unexpected error
                setStockMap(prev => {
                    const updatedMap = { ...prev };
                    Object.keys(updatedMap).forEach(sku => {
                        updatedMap[sku] = {
                            ...updatedMap[sku],
                            loading: false,
                        };
                    });
                    return updatedMap;
                });
            }
        };

        // Execute fetch immediately
        void fetchAllStockData();

        // Cleanup function to prevent state updates on unmounted component
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedBatchInputKey, fetchStockBatch]); // Use stable key instead of batchInput array

    return stockMap;
};
